import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import CreateAdminModal from "./CreateAdminModal";
import Flag from "react-world-flags";
import UserService from "../../../services/UserService";
import { countryCodes } from "../../utils/CommonFunctions";
import { HiOutlineUser, HiOutlineMail, HiOutlinePlus } from "react-icons/hi";
import axiosInstance from "../../../services/AxiosInstance";

const defaultCountryCode = {
  name: "India",
  code: "+91",
  iso: "IN",
};

const CreateOrganisation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId } = useParams();
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [name, setName] = useState("");
  const [admins, setAdmins] = useState([]);
  const [uniqueName, setUniqueName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState(null);
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locationSelected, setLocationSelected] = useState(false);
  const [userLocation, setUserLocation] = useState("");
  const [timezone, setTimezone] = useState("");
  const [searchCountryCode, setSearchCountryCode] = useState("");
  const [isCountryCodeOpen, setIsCountryCodeOpen] = useState(false);
  const handleCloseAdminModal = () => setShowAdminModal(false);
  const [selectedCountryCode, setSelectedCountryCode] =
    useState(defaultCountryCode);
  const [error, setError] = useState({
    errorFor: "",
    error: "",
  });
  const [togglingAdmin, setTogglingAdmin] = useState(null);
  const isSuperAdmin = location.pathname.includes("super-admin");

  const validateUniqueName = (value) => {
    const namePattern = /^[a-z_]+$/;

    if (!namePattern.test(value)) {
      setError({
        errorFor: "uniqueName",
        error:
          "Unique Name can only contain lowercase letters without spaces or special characters.",
      });
    } else {
      setError({
        errorFor: "",
        error: "",
      });
    }

    setUniqueName(value);
  };

  const isOnlyWhitespace = (str) => {
    return str.trim().length === 0;
  };

  const fetchOrgDetail = async () => {
    if (!orgId) {
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.get(`/v1/organization/${orgId}`);
      const data = response.data;

      setUniqueName(data.data.organization?.uniqueName ?? "");
      setName(data.data.organization.name);
      setUserLocation(data.data.organization.address);
      setPhone(data.data.organization.phone);
      setEmail(data.data.organization.email);
      setTimezone(data.data.organization.timeZone);
      setSelectedCountryCode(
        countryCodes.find(
          (countryCode) =>
            countryCode.code === data.data.organization.countryCode
        )
      );

      setAdmins(data.data.admins || []);
    } catch (error) {
      if (error.response?.data?.message?.includes("Cast to ObjectId failed")) {
        toast.error("Invalid Organization ID");
      } else {
        toast.error("An error occurred while fetching organization details.");
        navigate("/404");
      }
      console.error("Error fetching organization details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const resetForm = () => {
      setName("");
      setAddress("");
      setPhone("");
      setEmail("");
      setAdmins([]);
    };

    if (orgId) {
      fetchOrgDetail();
    } else {
      resetForm();
      setLoading(false);
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [orgId, navigate]);

  const handleClose = () => {
    {
      orgId
        ? navigate("/super-admin/list-organisation")
        : navigate("/super-admin");
    }
  };

  const handleValidationError = (message) => {
    if (message.includes("phone")) {
      if (message.includes("required") || message.includes("empty")) {
        return "Phone number can't be empty";
      }
      return "Phone number is invalid";
    }
    if (message.includes("name")) {
      if (message.includes("required") || message.includes("empty")) {
        return "Organisation name can't be empty";
      }
      return "Organisation Unique name is already taken. Please choose a different one.";
    }

    if (message.includes("address")) {
      if (message.includes("required") || message.includes("empty")) {
        return "Location can't be empty";
      }
      return "Location is invalid";
    }
    if (message.includes("email")) {
      if (message.includes("required") || message.includes("empty")) {
        return "Email can't be empty";
      }
      return "Email is invalid";
    }
    return "An unknown error occurred";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || isOnlyWhitespace(name)) {
      toast.error("Organisation name cannot be empty or contain only spaces");
      return;
    }

    if (phone && phone.length !== 10) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }

    if (error.error) {
      toast.error(error.error);
      return;
    }

    const url = orgId ? `/v1/organization/${orgId}` : `/v1/organization`;
    const method = orgId ? 'put' : 'post';

    try {
      setLoading(true);
      const response = await axiosInstance[method](url, {
        name: name.trim(),
        address: userLocation,
        countryCode: selectedCountryCode.code,
        phone,
        email: email.trim(),
      });

      const data = response.data;

      toast.success(
        orgId
          ? "Organisation Updated Successfully"
          : "New Organisation Created Successfully"
      );

      if (!orgId && data.data && data.data._id) {
        navigate(`/super-admin/edit-organisation/${data.data._id}`);
      } else {
        navigate("/super-admin/list-organisation");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message;
      if (errorMessage?.includes("E11000 duplicate key error")) {
        if (errorMessage.includes("name_1 dup key")) {
          toast.error(`An organisation with the name "${name}" already exists.`);
        } else if (errorMessage.includes("uniqueName_1 dup key")) {
          toast.error(`An organisation with the name "${uniqueName}" already exists.`);
        } else if (errorMessage.includes("email_1 dup key")) {
          toast.error(`An organisation with the email "${email}" already exists.`);
        } else if (errorMessage.includes("phone_1 dup key")) {
          toast.error(`An organisation with the phone number "${phone}" already exists.`);
        } else {
          toast.error("This organisation already exists.");
        }
      } else if (errorMessage?.includes("Name is already present")) {
        toast.error(errorMessage);
      } else if (errorMessage?.includes("Cast to ObjectId failed")) {
        toast.error("Invalid Organization ID");
      } else {
        const errorMessage = handleValidationError(error.response?.data?.message);
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredCountryCodes = (search) => {
    return countryCodes.filter((countryCode) =>
      countryCode.name.toLowerCase().includes(search.toLowerCase())
    );
  };

  useEffect(() => {
    if (location.pathname === "/super-admin/create-organisation") {
      setUniqueName("");
      setTimezone("");
      setSelectedCountryCode(defaultCountryCode);
      setUserLocation("");
      setError({
        errorFor: "",
        error: "",
      });
    }
  }, [location]);

  const handleCountryCodeClick = () => {
    setIsCountryCodeOpen(!isCountryCodeOpen);
  };

  const handleCountryCodeSelect = (value) => {
    setSelectedCountryCode(value);
    setSearchCountryCode("");
    setIsCountryCodeOpen(false);
  };

  const handleToggleAdmin = async (admin) => {
    const isActive = admin.isActive !== false && !admin.isDeactivated;
    setTogglingAdmin(admin._id);
    try {
      if (isActive) {
        await UserService.deactivateUser(admin._id, orgId);
        toast.success("Admin deactivated");
      } else {
        await UserService.activateUser(admin._id, orgId);
        toast.success("Admin activated");
      }
      fetchOrgDetail();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update admin");
    } finally {
      setTogglingAdmin(null);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest('.country-code-selector') === null) {
        setIsCountryCodeOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <>
      {loading && (
        <div className="fixed inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#166473]"></div>
        </div>
      )}
      <div className="flex items-center mb-6">
        <h3 className="text-2xl font-semibold text-[#166473]">
          {orgId ? "Edit Organisation" : "Add Organisation"}
        </h3>
      </div>
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organisation Name
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2.5 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#166473]/20 focus:border-[#166473] outline-none transition-colors"
                placeholder="Smarterview"
                aria-label="name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone<span className="text-red-500 ml-1">*</span>
              </label>
              <div className="flex gap-2">
                <div className="relative w-[140px] country-code-selector">
                  <div
                    className="flex items-center justify-between w-full px-4 py-2.5 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg cursor-pointer hover:border-[#166473] transition-colors"
                    onClick={handleCountryCodeClick}
                  >
                    <div className="flex items-center gap-2">
                      <Flag
                        code={selectedCountryCode?.iso}
                        className="h-5"
                      />
                      <span>{selectedCountryCode?.code}</span>
                    </div>
                    <svg
                      className={`w-4 h-4 transition-transform ${isCountryCodeOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {isCountryCodeOpen && (
                    <div className="absolute z-10 w-full min-w-[200px] mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                      <div className="p-2">
                        <input
                          type="text"
                          placeholder="Search"
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#166473]/20 focus:border-[#166473] outline-none"
                          value={searchCountryCode}
                          onChange={(e) => setSearchCountryCode(e.target.value)}
                        />
                      </div>
                      <div className="max-h-60 overflow-y-auto">
                        {filteredCountryCodes(searchCountryCode).length > 0 ? (
                          filteredCountryCodes(searchCountryCode).map((value) => (
                            <div
                              key={value.iso}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50"
                              onClick={() => handleCountryCodeSelect(value)}
                            >
                              <Flag code={value.iso} className="h-5 w-8" />
                              {value.code} {value.name}
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-2 text-sm text-gray-500">
                            No country code found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <input
                  type="text"
                  value={phone}
                  placeholder="9898998989"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,10}$/.test(value)) {
                      setPhone(value);
                    }
                  }}
                  name="phone"
                  className="flex-1 px-4 py-2.5 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#166473]/20 focus:border-[#166473] outline-none transition-colors"
                  aria-label="phone"
                  maxLength="10"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#166473]/20 focus:border-[#166473] outline-none transition-colors"
                placeholder="Example@gmail.com"
                aria-label="email"
              />
            </div>
            {orgId && (
              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admins
                </label>
                {admins.length > 0 && (
                  <div className="mt-4 overflow-hidden border border-gray-100 rounded-xl shadow-sm mb-4">
                    <div className="max-h-[300px] overflow-y-auto custom-scrollbar">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th
                              scope="col"
                              className="sticky top-0 z-30 bg-gray-50 px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                            >
                              Admin Details
                            </th>
                            <th
                              scope="col"
                              className="sticky top-0 z-30 bg-gray-50 px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                            >
                              Status
                            </th>
                            {isSuperAdmin && (
                              <th
                                scope="col"
                                className="sticky top-0 z-30 bg-gray-50 px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider"
                              >
                                Action
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {admins.map((admin, idx) => {

                            const isActive = admin.isActive !== false && !admin.isDeactivated;
                            return (
                              <tr key={admin._id || idx} className="hover:bg-gray-50/50 transition-colors">
                                <td className="px-5 py-4 whitespace-nowrap">
                                  <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#166473]/10 flex items-center justify-center text-[#166473]">
                                      <HiOutlineUser size={18} />
                                    </div>
                                    <div>
                                      <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                        {admin.name}
                                        {idx === 0 && (
                                          <span className="px-2 py-0.5 text-[10px] bg-indigo-50 text-indigo-600 rounded-full font-medium">Primary</span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-1.5 mt-0.5 text-xs text-gray-500">
                                        <HiOutlineMail size={14} className="opacity-70" />
                                        <span>{admin.email}</span>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-5 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${isActive
                                    ? "bg-green-50 text-green-700 border border-green-100"
                                    : "bg-red-50 text-red-700 border border-red-100"
                                    }`}>
                                    <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${isActive ? "bg-green-500" : "bg-red-500"}`}></span>
                                    {isActive ? "Active" : "Deactivated"}
                                  </span>
                                </td>
                                {isSuperAdmin && (
                                  <td className="px-5 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button
                                      onClick={() => handleToggleAdmin(admin)}
                                      disabled={togglingAdmin === admin._id}
                                      className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#166473] focus:ring-offset-1 disabled:cursor-wait disabled:opacity-50 ${isActive ? "bg-[#166473]" : "bg-gray-200"
                                        }`}
                                    >
                                      <span
                                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition ${isActive ? "translate-x-4" : "translate-x-0"
                                          }`}
                                      />
                                    </button>
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                <CreateAdminModal
                  show={showAdminModal}
                  onHide={handleCloseAdminModal}
                  onAdminCreated={() => {
                    fetchOrgDetail();
                    handleCloseAdminModal();
                  }}
                  orgId={orgId}
                  buttonLabel={admins.length > 0 ? "Add Another Administrator" : "Add Administrator"}
                />
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 rounded-b-xl border-t border-gray-200">
          <div className="flex justify-end gap-3">
            <button
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-200 focus:outline-none transition-colors"
              onClick={handleClose}
            >
              Close
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className="px-6 py-2.5 text-sm font-medium text-white bg-[#166473] rounded-lg hover:bg-[#1a7a8c] focus:ring-4 focus:ring-[#166473]/20 focus:outline-none transition-colors"
            >
              {orgId ? "Update Organisation" : "Create Organisation"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateOrganisation;
